import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Row,
  Col,
  Table,
  Badge,
  Dropdown,
  Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import DOMPurify from "dompurify";
import { DETAILS_BG, filterPopoverStyles, tableStyles } from "Constants/Style";
import {
  fetchProductSalesOrderById,
  fetchServiceSalesOrderById,
} from "slices/sales_order/thunk";
import { MecordColors } from "Constants/Color";
import CancelBackButton from "Common/Components/Buttons/CancelBackButton";
import StatusRemarksHistory from "pages/Enquiry/Components/StatusRemarksHistory";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import ProductHistoryModal from "Common/Components/ProductHistoryModal/ProductHistoryModal";
import LoadingSpinner from "components/LoadingSpinner";
import moment from "moment";
import { AttachmentList } from "components/DocumentListing/AttachmentListing";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import ViewServiceAccessoriesModal from "pages/SalesOrder/Services/Forms/Components/ViewServiceAccessoriesModal";
import { mapApiAccessoryToItem } from "pages/SalesOrder/Services/Forms/Components/ServiceAccessoryRow";

const ViewSalesOrderPage: React.FC = () => {
  const { id, activeType } = useParams<{ id: string; activeType: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<any>();

  // Extract tab from URL path (e.g., /sales_order/service/draft/view/123 -> "draft")
  // or /sales_order/service/sales-order-log/view/123 -> "sales-order-log"
  const getBackPath = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Expected structure: sales_order, service, tab, view, id
    const serviceIndex = pathSegments.findIndex((seg) => seg === "service");
    if (serviceIndex === -1) {
      // Fallback if service not found in path
      return `/sales_order/service/sales_order_log`;
    }
    const tab = pathSegments[serviceIndex + 1] || "sales-order-log"; // Default to sales-order-log if not found
    
    // Map tab to the correct route (handle both hyphen and underscore variants)
    if (tab === "draft") {
      return `/sales_order/service/draft`;
    } else if (tab === "sales-order-log" || tab === "sales_order_log") {
      return `/sales_order/service/sales_order_log`;
    } else if (tab === "junk") {
      return `/sales_order/service/junk`;
    }
    // Default fallback
    return `/sales_order/service/sales_order_log`;
  };

  // Redux state with safe fallback
  const salesOrderState = useSelector(
    (state: any) =>
      state.SalesOrder || {
        currentSalesOrder: state,
        loading: false,
        error: null,
      },
  );

  const chunkArray = (arr: any[], size = 2) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };


  const { currentSalesOrder, loading, error } = salesOrderState;
  // console.log("currentSalesOrder : => ", currentSalesOrder);

  // Product History Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedSerialNo, setSelectedSerialNo] = useState<string>("");
  const [productHistory, setProductHistory] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Description & Additional Details modals (view only)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionModalContent, setDescriptionModalContent] = useState("");
  const [descriptionModalServiceName, setDescriptionModalServiceName] = useState("");
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsContent, setAdditionalDetailsContent] = useState("");
  const [additionalDetailsServiceName, setAdditionalDetailsServiceName] = useState("");
  const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);
  const [viewAccessories, setViewAccessories] = useState<any[]>([]);
  const [accessoriesServiceName, setAccessoriesServiceName] = useState("");
  const [accessoriesServiceCategory, setAccessoriesServiceCategory] = useState("");

  // Fetch product history
  const fetchProductHistory = async (serialNo: string) => {
    if (!serialNo || serialNo === "-") {
      toast.error("Invalid serial number");
      return;
    }

    setLoadingHistory(true);
    setSelectedSerialNo(serialNo);
    setShowHistoryModal(true);

    try {
      const response = await Axios.get(
        API_ENDPOINT.INVENTORY.PRODUCT_HISTORY(serialNo),
      );
      setProductHistory(response.data?.data || response.data);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to fetch product history",
      );
      setProductHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  // const dummySalesOrder = {
  //   id: 101,
  //   sales_order_number: "SO-2024-00125",
  //   status: "pending",
  //   initiated_by: "Rahul Sharma",
  //   order_date: "2024-09-15",
  //   eta_date: "2024-09-25",
  //   industry: "Manufacturing",
  //   company_name: "Mecord Systems Pvt Ltd",

  //   contact_person_name: "Amit Verma",
  //   contact_person_email: "amit.verma@mecord.com",
  //   contact_person_phone_no: "9876543210",

  //   terms_and_conditions: "Payment within 30 days from invoice date.",
  //   notes: "Handle with care during transportation.",
  //   payment_terms: "30 Days Credit",

  //   contact_persons: [
  //     {
  //       contact_person_name: "Amit Verma",
  //       contact_person_email: "amit.verma@mecord.com",
  //       contact_person_phone: "9876543210",
  //     },
  //     {
  //       contact_person_name: "Neha Singh",
  //       contact_person_email: "neha.singh@mecord.com",
  //       contact_person_phone: "9123456780",
  //     },
  //   ],

  //   attachments: [
  //     {
  //       file_url: "https://example.com/po_documents/PO_125.pdf",
  //       file_name: "Customer_PO_125.pdf",
  //     },
  //   ],

  //   item_details: [
  //     {
  //       id: 1,
  //       item_name: "Industrial Pressure Sensor",
  //       category_name: "Sensors",
  //       sub_category_name: "Pressure Sensors",
  //       make_name: "Bosch",
  //       model_name: "PS-900X",
  //       product_code: "BOS-PS-900X",

  //       is_serviceable: true,
  //       installation_required: true,
  //       inspection_required: true,

  //       installation_date: "2024-09-20",
  //       inspection_date: "2024-09-22",

  //       quantity: 10,
  //       unit: "Nos",
  //       unit_price: 4500,
  //       discount_type: "Percentage",
  //       discount_value: 500,
  //       net_value: 4000,

  //       gst_percent: 18,
  //       sgst_percent: 9,
  //       cgst_percent: 9,
  //       igst_percent: 0,

  //       gst_value: 720,
  //       total: 4720,

  //       shipping_address: {
  //         shipping_id: 501,
  //         address: "Plot No. 23, MIDC Industrial Area",
  //         city: "Pune",
  //         state: "Maharashtra",
  //         country: "India",
  //         pin_code: "411026",
  //         phone_number: "020-27654321",
  //       },

  //       item_availability: "In Stock",
  //     },
  //     {
  //       id: 2,
  //       item_name: "Temperature Controller",
  //       category_name: "Controllers",
  //       sub_category_name: "Temperature Controllers",
  //       make_name: "Siemens",
  //       model_name: "TC-450",
  //       product_code: "SIE-TC-450",

  //       is_serviceable: false,
  //       installation_required: false,
  //       inspection_required: false,

  //       installation_date: null,
  //       inspection_date: null,

  //       quantity: 5,
  //       unit: "Nos",
  //       unit_price: 7800,
  //       discount_type: "Flat",
  //       discount_value: 800,
  //       net_value: 7000,

  //       gst_percent: 18,
  //       sgst_percent: 9,
  //       cgst_percent: 9,
  //       igst_percent: 0,

  //       gst_value: 1260,
  //       total: 8260,

  //       shipping_address: {
  //         shipping_id: 502,
  //         address: "Unit 5, Electronic City",
  //         city: "Bengaluru",
  //         state: "Karnataka",
  //         country: "India",
  //         pin_code: "560100",
  //         phone_number: "080-44556677",
  //       },

  //       item_availability: "Limited Stock",
  //     },
  //   ],

  //   agent: {
  //     agent_name: "Suresh Patil",
  //   },
  //   billing_address: null,
  // };

  // Extract the actual sales order data (handle both nested and direct structures)
  const salesOrder = currentSalesOrder?.data || currentSalesOrder;

  // Get contact details from customer_details
  const customerDetails = salesOrder?.customer_details || {};
  const contactPersons = customerDetails?.contact_details || [];
  const contactDetails = contactPersons?.[0] || {};

  // Get agent details
  const agent = { agent_name: salesOrder?.agent_name || "-" };

  // Get billing addresses
  const billFrom = salesOrder?.bill_from || {};
  const billTo = salesOrder?.bill_to || {};

  // Format date helper - converts UTC to local date for display using moment
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = moment(dateString);
      if (!date.isValid()) return "-";
      return date.format("DD-MMM-YYYY");
    } catch {
      return "-";
    }
  };

  // Format date as DD-MM-YYYY for retention follow-up date - converts UTC to local date using moment
  const formatDateDDMMYYYY = (
    dateString: string | null | undefined,
  ): string => {
    if (!dateString) return "-";
    try {
      const date = moment(dateString);
      if (!date.isValid()) return "-";
      return date.format("DD-MM-YYYY");
    } catch {
      return "-";
    }
  };

  // Helper function to get follow-up date by category from sales_order_follow_ups
  const getFollowUpDateByCategory = (
    category: string,
    useNextFollowUpDate: boolean = true,
  ): string | null => {
    if (
      !salesOrder?.sales_order_follow_ups ||
      !Array.isArray(salesOrder.sales_order_follow_ups)
    ) {
      return null;
    }

    // Case-insensitive search for category
    const followUp = salesOrder.sales_order_follow_ups.find(
      (item: any) =>
        item.follow_up_category?.toLowerCase() === category.toLowerCase(),
    );

    if (!followUp) return null;

    if (useNextFollowUpDate && followUp.next_follow_up_date) {
      return followUp.next_follow_up_date;
    }

    if (followUp.follow_up_date) {
      return followUp.follow_up_date;
    }

    return null;
  };

  // Format terms and conditions array
  const formatTermsAndConditions = (
    terms: any[] | string | null | undefined,
  ): string | React.ReactNode | null => {
    if (!terms) return null;
    
    // If it's already a string, return it
    if (typeof terms === "string") {
      // Check if it's a JSON string
      if (terms.trim().startsWith("[") || terms.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(terms);
          if (Array.isArray(parsed)) {
            return formatTermsArray(parsed);
          }
          return terms;
        } catch {
          return terms;
        }
      }
      return terms;
    }
    
    // If it's an array, format it
    if (Array.isArray(terms)) {
      if (terms.length === 0) return null;
      return formatTermsArray(terms);
    }
    
    // Fallback
    return null;
  };

  // Helper function to format terms array
  const formatTermsArray = (termsArray: any[]): React.ReactNode => {
    if (!Array.isArray(termsArray) || termsArray.length === 0) {
      return null;
    }

    // Group terms by type to handle multiple options per type
    const groupedTerms: Record<string, string[]> = {};
    termsArray.forEach((item: any) => {
      if (item.type) {
        if (!groupedTerms[item.type]) {
          groupedTerms[item.type] = [];
        }
        // Handle both string and array options
        if (Array.isArray(item.options)) {
          item.options.forEach((opt: string) => {
            if (opt && !groupedTerms[item.type].includes(opt)) {
              groupedTerms[item.type].push(opt);
            }
          });
        } else if (item.options) {
          // If options is a string, check if it's newline-separated
          const options = item.options
            .split("\n")
            .filter((opt: string) => opt.trim());
          options.forEach((opt: string) => {
            if (opt && !groupedTerms[item.type].includes(opt)) {
              groupedTerms[item.type].push(opt);
            }
          });
        }
      }
    });

    return (
      <>
        {Object.entries(groupedTerms).map(([type, options], idx) => (
            <div key={idx} className="mb-3">
              <strong
                style={{
                  fontSize: "0.75rem",
                  display: "block",
                  marginBottom: "0.5rem",
                }}
              >
                {type}
              </strong>
              <ul
                style={{
                  fontSize: "0.7rem",
                  color: "#666",
                  marginLeft: "20px",
                  paddingLeft: "0",
                  listStyle: "disc",
                }}
              >
              {options.map((option: string, optIdx: number) => (
                <li key={optIdx} style={{ marginBottom: "4px" }}>
                      {option}
                    </li>
              ))}
              </ul>
            {idx < Object.keys(groupedTerms).length - 1 && (
                <hr
                  style={{
                    width: "100%",
                    marginTop: "12px",
                  }}
                />
              )}
            </div>
        ))}
      </>
    );
  };

  // Transform status remarks and history from backend to UI format
  const transformStatusRemarks = (remarks: any[] | null | undefined) => {
    if (!remarks || !Array.isArray(remarks) || remarks.length === 0) {
      return [];
    }

    const statusColors: { [key: string]: string } = {
      "In Progress": "#0d6efd",
      Pending: "#ffc107",
      Completed: "#198754",
      Delivered: "#198754",
      "On Hold": "#dc3545",
      Draft: "#6c757d",
      Closed: "#198754",
    };

    const colors = [
      { bg: "#fee2e2", circle: "#ef4444", text: "#991b1b" },
      { bg: "#ccfbf1", circle: "#0d9488", text: "#115e59" },
      { bg: "#e0e7ff", circle: "#4f46e5", text: "#3730a3" },
      { bg: "#fef3c7", circle: "#d97706", text: "#92400e" },
    ];

    return remarks.map((item: any, index: number) => {
      const username = item.username || "-";
      const nameParts = username.split(" ");
      const initials =
        nameParts.length >= 2
          ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
          : username.substring(0, 2).toUpperCase();

      const status = item.sales_order_status || "-";
      const statusColor = statusColors[status] || "#6c757d";

      // Convert UTC date to local date using moment
      const date = item.created_at ? moment(item.created_at) : moment();

      // Validate date before formatting
      if (!date.isValid()) {
        return {
          employee_name: username,
          initials: initials,
          status: status,
          statusColor: statusColor,
          color: colors[index % colors.length].circle,
          date: "-",
          time: "-",
          comment: item.remark || item.comment || "-",
        };
      }

      const formattedDate = date.format("DD-MMM-YYYY");
      const formattedTime = date.format("hh:mm A");

      const color = colors[index % colors.length];

      return {
        employee_name: username,
        initials: initials,
        status: status,
        statusColor: statusColor,
        color: color.circle,
        date: formattedDate,
        time: formattedTime,
        comment: item.remark || item.comment || "-",
      };
    });
  };

  // Fetch sales order details if salesOrderNo present
  useEffect(() => {
    if (id) {
      dispatch(fetchServiceSalesOrderById(id));
    }
  }, [dispatch, id, activeType]);

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    try {
      // TODO: Implement status update API call
      toast.success(`Sales Order marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <>
      <style>
        {`
      .followup-box {
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #fff;
  padding: 12px;
}

/* Scrollable area */
.followup-scroll {
  max-height: 360px; /* ~4 comments */
  overflow-y: auto;
}

/* Card */
.followup-card {
  padding: 12px;
  border-radius: 8px;
  background-color: #f8f9fa;
  margin-bottom: 10px;
}

/* Avatar */
.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

/* Status badge */
.status-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  color: #fff;
}

/* Comment text */
.comment-text {
  font-size: 14px;
  color: #212529;
  line-height: 1.4;
}

/* Scrollbar (optional) */
.followup-scroll::-webkit-scrollbar {
  width: 6px;
}

.followup-scroll::-webkit-scrollbar-thumb {
  background-color: #ced4da;
  border-radius: 10px;
}

/* Keep description/additional-details modal content (including images) inside modal */
.description-modal-content {
  overflow: auto;
  max-width: 100%;
  max-height: 70vh;
  word-wrap: break-word;
}
.description-modal-content img {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

      `}
      </style>

      <div className="page-content p-4" style={{ marginTop: "70px" }}>
        <Card className="shadow-sm rounded-3 border-0">
          <Card.Body>
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h5 className="fw-bold d-flex align-items-center mb-0">
                <i
                  className="ph-arrow-left me-2"
                  role="button"
                  onClick={() => navigate(-1)}
                ></i>
                View Service Order
              </h5>
              {id && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/sales_order/service/edit/${id}`)}
                  style={{ backgroundColor: MecordColors?.ThemeBGColor, borderColor: MecordColors?.ThemeBGColor }}
                >
                  <i className="ri-pencil-line me-1"></i> Edit Sales Order
                </Button>
              )}
            </div>

            <hr />

            {/* Loading State */}
            {loading && (
              <LoadingSpinner themeColor={MecordColors?.ThemeBGColor} />
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="text-center text-danger py-5">
                <h5>
                  {typeof error === "string"
                    ? error 
                    : error?.message ||
                      error?.error ||
                      "Failed to fetch sales order details"}
                </h5>
              </div>
            )}

            {/* Not Found State */}
            {!salesOrder && !loading && !error && (
              <div className="text-center py-5">
                <h5>Service Order not found</h5>
              </div>
            )}

            {/* Content - Only show when not loading, no error, and salesOrder exists */}
            {!loading && !error && salesOrder && (
              <>
            {/* Sales Order Details OR Service */}
                <h5 className="mb-3">Service Order Details</h5>
            <Row className="mb-4 ms-3">
              <Col md={6}>
                <p>
                      <strong>
                        SRV No. :{" "}
                        {salesOrder?.sales_order_number ||
                          salesOrder?.sr_no ||
                          "-"}
                      </strong>{" "}
                    </p>
                    {salesOrder?.pi_number && (
                      <p>
                        <strong>PI Number :</strong> {salesOrder.pi_number}
                      </p>
                    )}
                <p>
                  <strong>Service Number :</strong>{" "}
                  {salesOrder?.service_number || "-"}
                </p>
                {salesOrder?.uid_number && (
                  <p>
                        <strong>UID Number :</strong> {salesOrder?.uid_number}
                  </p>
                )}
                <p>
                  <strong>Initiated By :</strong>{" "}
                  {salesOrder?.initiated_by || "-"}
                </p>
                <p>
                  <strong>Enquiry No. :</strong>{" "}
                  {salesOrder?.enquiry_number || "-"}
                </p>
                <p>
                  <strong>Quotation No. :</strong>{" "}
                  {salesOrder?.quotation_number || "-"}
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>SRF Date :</strong>{" "}
                      {formatDate(
                        salesOrder?.sales_order_date || salesOrder?.srv_date,
                      )}
                </p>
                <p>
                  <strong>Schedule Service Date :</strong>{" "}
                      {formatDate(
                        salesOrder?.schedule_service_date ||
                          salesOrder?.valid_until_date,
                      )}
                </p>
                {/* <p>
                  <strong>Status :</strong>{" "}
                  <Badge
                    bg={
                      salesOrder?.sales_order_status === "draft"
                        ? "secondary"
                        : "primary"
                    }
                  >
                    {salesOrder?.sales_order_status || "-"}
                  </Badge>
                </p> */}
              </Col>
            </Row>

            <hr />
            {/* Company Details */}
            <h5 className="mb-3">Customer Details</h5>
            <Row
              className="mb-4 ms-1 me-1 p-3"
              rounded
              style={{
                backgroundColor: MecordColors.ActiveBackground,
                borderRadius: "10px",
              }}
            >
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Company Name :</strong>{" "}
                    {customerDetails?.company_name ||
                      customerDetails?.customer_name ||
                      "-"}
                  </p>
                  <p>
                    <strong>Company Email :</strong>{" "}
                    {customerDetails?.company_email ||
                      customerDetails?.email ||
                      "-"}
                  </p>
                  <p>
                    <strong>Customer PO Number :</strong>{" "}
                    {customerDetails?.customer_po_number ||
                      salesOrder?.phone_number ||
                      "-"}
                  </p>
                  <p>
                    <strong>PO Document :</strong>{" "}
                    {customerDetails?.customer_po_documents &&
                    Array.isArray(customerDetails.customer_po_documents) &&
                    customerDetails.customer_po_documents.length > 0 ? (
                      <div>
                        {customerDetails.customer_po_documents.map(
                          (doc: string, index: number) => (
                            <div key={index} className="mb-1">
                              <a
                                href={doc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-decoration-underline"
                              >
                                View Document {index + 1}
                              </a>
                            </div>
                              ),
                        )}
                      </div>
                    ) : customerDetails?.customer_po_document_url ? (
                      <a
                        href={customerDetails.customer_po_document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-underline"
                      >
                        View Document
                      </a>
                    ) : (
                      "-"
                    )}
                  </p>
                </Col>

                <Col md={6}>
                  <p>
                    <strong>Industry :</strong>{" "}
                    {customerDetails?.industry ||
                      billFrom?.industry_type ||
                      "-"}
                  </p>
                  <p>
                    <strong>Company Phone No. :</strong>{" "}
                    {customerDetails?.company_phone_no ||
                      customerDetails?.phone_number ||
                      "-"}
                  </p>
                  <p>
                    <strong>PO Date :</strong>{" "}
                    {formatDate(customerDetails?.customer_po_date)}
                  </p>
                </Col>
              </Row>

              <hr />
              <h5 className="mb-3 mt-3">Contact Details</h5>
              <Row>
                    {Array.isArray(contactPersons) &&
                    contactPersons.length > 0 ? (
                  chunkArray(contactPersons, 2).map((row, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                      {row.map((contact: any, colIndex: number) => (
                        <Col md={6} key={colIndex} className="mb-3">
                          <div className="p-2">
                            <p className="mb-1">
                              <strong>Contact Name :</strong>{" "}
                              {contact?.contact_person_name ||
                                contact?.name ||
                                "-"}
                            </p>
                            <p className="mb-1">
                              <strong>Email :</strong>{" "}
                              {contact?.contact_person_email ||
                                contact?.email ||
                                    "-"}
                            </p>
                            <p className="mb-1">
                              <strong>Phone Number :</strong>{" "}
                              {contact?.contact_person_phone_no ||
                                contact?.phone_number ||
                                  "-"}
                            </p>
                            {contact?.designation && (
                              <p className="mb-1">
                                <strong>Designation :</strong>{" "}
                                {contact.designation}
                              </p>
                            )}
                          </div>
                        </Col>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <Col md={6}>
                    <p>
                      <strong>Contact Name :</strong>{" "}
                      {contactDetails?.contact_person_name ||
                        contactDetails?.name ||
                          "-"}
                    </p>
                    <p>
                      <strong>Email :</strong>{" "}
                      {contactDetails?.contact_person_email ||
                        contactDetails?.email ||
                          "-"}
                    </p>
                    <p>
                      <strong>Phone Number :</strong>{" "}
                      {contactDetails?.contact_person_phone_no ||
                        contactDetails?.phone_number ||
                          "-"}
                    </p>
                  </Col>
                )}
              </Row>
            </Row>

            <hr />
        
            {/* Agent Details */}
         <Row className="mb-4">
              <Col md={6}>
                <h5 className="mb-3">Agent Details</h5>
                <p className="ms-4">
                  <strong>Agent Name :</strong> {agent.agent_name || "-"}
                </p>
              </Col>

              <Col md={6}>
                <h5 className="mb-3">Assign Member</h5>
                <div className="d-flex flex-wrap gap-2 ms-4">
                  {salesOrder.assigned_members &&
                  salesOrder.assigned_members.length > 0 ? (
                    salesOrder.assigned_members.map(
                      (member: any, idx: number) => {
                        const initials = `${member.first_name?.[0] || ""}${member.last_name?.[0] || ""
                        }`.toUpperCase();
                        const colors = [
                          { bg: "#fee2e2", circle: "#ef4444", text: "#991b1b" },
                          { bg: "#ccfbf1", circle: "#0d9488", text: "#115e59" },
                          { bg: "#e0e7ff", circle: "#4f46e5", text: "#3730a3" },
                          { bg: "#fef3c7", circle: "#d97706", text: "#92400e" },
                        ];
                        const color = colors[idx % colors.length];

                        return (
                          <div
                            key={member.user_id || idx}
                            className="d-flex align-items-center p-1 pe-3 rounded"
                            style={{
                              backgroundColor: color.bg,
                              border: "none",
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded me-2"
                              style={{
                                width: "24px",
                                height: "24px",
                                backgroundColor: color.circle,
                                color: "#fff",
                                fontSize: "0.7rem",
                                fontWeight: "bold",
                              }}
                            >
                              {initials || "U"}
                            </div>
                            <span
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                color: "#1e293b",
                              }}
                            >
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                        );
                      }
                    )
                  ) : (
                    <span className="text-muted">No members assigned</span>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
              
              <h5 className="mb-3">Tags</h5>
              <div className="d-flex flex-wrap gap-2 ms-4">
              {Array.isArray(salesOrder?.tags) &&
                    salesOrder.tags.length > 0 ? (
                      <span className="d-inline-flex flex-wrap gap-2 mt-1">
                        {salesOrder.tags.map((tag: string) => (
                          <span
                            key={tag}
                            className="badge bg-light text-dark d-flex align-items-center rounded-2"
                            style={{
                              border: "1px solid #dee2e6",
                              padding: "4px 8px",
                              fontSize: "11px",
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                </div>
              </Col>
            </Row>


            {/* Technician Details for Service */}
 
              <>
                <hr />
                <h5 className="mb-3">Engineer Details</h5>
                <Row className="mb-4 ms-3">
                  <Col md={6}>
                  <div className="d-flex flex-wrap gap-2 ms-2">
                        {salesOrder.assigned_technician &&
                  salesOrder.assigned_technician.length > 0 ? (
                    salesOrder.assigned_technician.map(
                      (member: any, idx: number) => {
                              const initials =
                                `${member.first_name?.[0] || ""}${
                          member.last_name?.[0] || ""
                        }`.toUpperCase();
                        const colors = [
                                {
                                  bg: "#fee2e2",
                                  circle: "#ef4444",
                                  text: "#991b1b",
                                },
                                {
                                  bg: "#ccfbf1",
                                  circle: "#0d9488",
                                  text: "#115e59",
                                },
                                {
                                  bg: "#e0e7ff",
                                  circle: "#4f46e5",
                                  text: "#3730a3",
                                },
                                {
                                  bg: "#fef3c7",
                                  circle: "#d97706",
                                  text: "#92400e",
                                },
                        ];
                        const color = colors[idx % colors.length];

                        return (
                          <div
                            key={member.user_id || idx}
                            className="d-flex align-items-center p-1 pe-3 rounded"
                            style={{
                              backgroundColor: color.bg,
                              border: "none",
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded me-2"
                              style={{
                                width: "24px",
                                height: "24px",
                                backgroundColor: color.circle,
                                color: "#fff",
                                fontSize: "0.7rem",
                                fontWeight: "bold",
                              }}
                            >
                              {initials || "U"}
                            </div>
                            <span
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                color: "#1e293b",
                              }}
                            >
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                        );
                            },
                    )
                  ) : (
                          <span className="text-muted">
                            No members assigned
                          </span>
                  )}
                </div>
                  </Col>
                </Row>
              </>

            <hr />
            {/* PBG (Performance Bank Guarantee) Details */}
            <h5 className="mb-3">PBG (Performance Bank Guarantee) Details</h5>
            <Row className="mb-4 ms-3">
              <Col md={6}>
                <p>
                  <strong>PBG Applicable :</strong>{" "}
                  {salesOrder?.pbg_applicable ? "Yes" : "No"}
                </p>
                <p>
                  <strong>PBG Date :</strong>{" "}
                  {(() => {
                    const pbgFollowUpDate = getFollowUpDateByCategory("Pbg", true);
                    if (pbgFollowUpDate) {
                      return formatDate(pbgFollowUpDate);
                    }
                    // Fallback to pbg_date if no follow-up found
                    return formatDate(salesOrder?.pbg_date);
                  })()}
                </p>
                <p>
                  <strong>Amount:</strong>{" "}
                  {salesOrder?.pbg_amount
                    ? `${"₹"}${formatIndianAmount(salesOrder.pbg_amount)}`
                    : "-"}
                </p>
                <p>
                  <strong>PBG Status :</strong> {salesOrder?.pbg_status || "-"}
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>PBG Number :</strong>{" "}
                    {salesOrder?.pbg_number || "-"}
                </p>
                <p>
                  <strong>Bank Name :</strong> {salesOrder?.bank_name || "-"}
                </p>
                <p>
                  <strong>Validity :</strong>{" "}
                  { formatDate(salesOrder?.pbg_validity)|| "-"}
                </p>
                <p>
                  <strong>Upload Document :</strong>{" "}
                  {salesOrder?.pbg_documents &&
                  Array.isArray(salesOrder.pbg_documents) &&
                  salesOrder.pbg_documents.length > 0 ? (
                    <div>
                      {salesOrder.pbg_documents.map(
                        (doc: string, index: number) => (
                          <div key={index} className="mb-1">
                            <a
                              href={doc}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-underline"
                            >
                              View Document {index + 1}
                            </a>
                          </div>
                        )
                      )}
                    </div>
                  ) : typeof salesOrder?.pbg_documents === "string" ? (
                    <a
                      href={salesOrder.pbg_documents}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-underline"
                    >
                      View Document
                    </a>
                  ) : (
                    "-"
                  )}
                </p>
              </Col>
            </Row>



            <hr />
            <Row>
            <h5 className="mb-3">EMD (Earnest Money Deposit) Details</h5>
            <Col md={6}  className="ms-3">
            <Row>
            <p>
                  <strong>EMD Mode :</strong>{" "}
                  {salesOrder?.mode_of_emd || "-"}
                </p>
            </Row>
            <Row>
             
                <p>
                  <strong>EMD Amount :</strong>{" "}
                  {salesOrder?.emd_amount
                    ? `${"₹"}${formatIndianAmount(salesOrder.emd_amount)}`
                    : "-"}
                </p>
                </Row>
              </Col>
            </Row>
            {/* GST & Taxation Details */}
            {/* <h5 className="mb-3">GST & Taxation Details</h5>
            <Row className="mb-4 ms-3">
              <Col md={6}>
                <p>
                  <strong>Compositional GST :</strong>{" "}
                  {salesOrder?.composition_gst ? "Yes" : "No"}-   
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>GST % :</strong>{" "}
                  {salesOrder?.select_composition_gst || "-"}
                </p>
              </Col>
            </Row>

            <hr /> */}
     
            {/* Inspection & Installation Details */}
            {/* <h5 className="mb-3">Inspection & Installation Details</h5>
            <Row className="mb-4 ms-3">
              <Col md={6}>
                <p>
                  <strong>Inspection :</strong>{" "}
                  {salesOrder?.inspection || "-"}
                </p>
                <p>
                  <strong>Installation :</strong>{" "}
                  {salesOrder?.installation || "-"}
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>Inspection Date :</strong>{" "}
                  {formatDate(salesOrder?.inspection_date)}
                </p>
                <p>
                  <strong>Installation Date :</strong>{" "}
                  {formatDate(salesOrder?.installation_date)}
                </p>
              </Col>
            </Row> */}

            <hr />
            {/* Billing Details */}
            <h5 className="mb-3">Billing Details</h5>
            <Row
              className="ms-1 p-3"
              rounded
              style={{
                backgroundColor: MecordColors.ActiveBackground,
                borderRadius: "10px",
              }}
            >
              <Col md={6}>
                <p>
                  <strong>Bill From :</strong>{" "}
                </p>
                {billFrom?.org_address && (
                  <p className="mt-1 text-muted small">
                    {billFrom.org_address}, {billFrom.org_city},{" "}
                    {billFrom.org_state} - {billFrom.org_pincode}
                  </p>
                )}
              </Col>
              <Col md={6}>
                <p>
                  <strong>Bill To :</strong>{" "}
           
                </p>
                  {billTo?.address && (
                  <p className="mt-1 text-muted small">
                    {billTo.address}, {billTo.city}, {billTo.state} -{" "}
                    {billTo.pincode || billTo.pin_code}
                  </p>
                )}
              </Col>
            </Row>

            <hr />
            {/* Products/Items */}
            <h5 className="mb-3">Services</h5>
            <div
              className="table-responsive p-3 mb-4"
              style={{
                backgroundColor: MecordColors.ActiveBackground,
                borderRadius: "10px",
              }}
            >
              <div
                className="overflow-auto"
                style={{}}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: "2400px",
                    borderCollapse: "collapse",
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Sr No.
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Products
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Additional Details
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Make
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Model
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Serial No.
                      </th>
                           <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        SAC Code.
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Service Category
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Service Method
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Service Type
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Execution Mode
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Product Quantity
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Service Rate
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Gross Value
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Discount/Flat
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Discount Value
                      </th>
                      <th style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "left" }}>Net Value</th>

                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >

                        GST(%)
                      </th>
                      {/* <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Composition GST(%)
                      </th> */}
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        SGST(%)
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        CGST(%)
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        IGST(%)
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        GST Value
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Total
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Service Address
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                          textAlign: "left",
                        }}
                      >
                        Reported Complaint
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: "#fff" }}>
                    {salesOrder?.sales_order_service_items?.length > 0 ? (
                      salesOrder.sales_order_service_items.map(
                        (item: any, idx: number) => (
                          <tr key={item.id || item.item_id || idx}>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                                verticalAlign: "middle",
                              }}
                            >
                              <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
                                <div className="d-flex align-items-center gap-1">
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {item.product_name || "-"}
                                    <div
                                  style={{
                                    color: "#666",
                                    fontSize: "0.65rem",
                                  }}
                                >
                                  SKU: {item.product_sku || item.sku || "-"} | SAC Code:{" "}
                                  {item.sac_code || "-"}
                                </div>
                                  </div>
                                  <InfoIcon
                                    style={{
                                      cursor: "pointer",
                                      color: tableStyles.primaryColor || MecordColors?.ThemeBGColor,
                                      fontSize: "18px",
                                      flexShrink: 0,
                                    }}
                                    onClick={() => {
                                      const content = item.prod_description ?? item.description ?? "";
                                      setDescriptionModalContent(content);
                                      setDescriptionModalServiceName(item.product_name || "");
                                      setShowDescriptionModal(true);
                                    }}
                                  />
                                </div>
                                {Array.isArray(item.accessories) && item.accessories.filter((a: any) => !a.is_deleted).length > 0 && (
                                  <Button
                                    variant="link"
                                    className="p-0 mt-1"
                                    style={{
                                      fontSize: "0.7rem",
                                      fontFamily: "Poppins, sans-serif",
                                      color: MecordColors?.ThemeBGColor || "#AB2123",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() => {
                                      const accs = (item.accessories || [])
                                        .filter((a: any) => !a.is_deleted)
                                        .map(mapApiAccessoryToItem);
                                      setViewAccessories(accs);
                                      setAccessoriesServiceName(item.product_name || "");
                                      setAccessoriesServiceCategory(item.service_category || "");
                                      setShowAccessoriesModal(true);
                                    }}
                                  >
                                    View Accessories ({item.accessories.filter((a: any) => !a.is_deleted).length})
                                  </Button>
                                )}
                          
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                                verticalAlign: "middle",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <InfoIcon
                                  style={{
                                    cursor: "pointer",
                                    color: tableStyles.primaryColor || MecordColors?.ThemeBGColor,
                                    fontSize: "18px",
                                  }}
                                  onClick={() => {
                                    const content = item.additional_details ?? "";
                                    setAdditionalDetailsContent(content);
                                    setAdditionalDetailsServiceName(item.product_name || "");
                                    setShowAdditionalDetailsModal(true);
                                  }}
                                />
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.make_name || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.model_name || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span>{item.serial_number || "-"}</span>
                                {item.serial_number && item.serial_number !== "-" && (
                                  <InfoIcon
                                    style={{
                                      cursor: "pointer",
                                      color: MecordColors?.ThemeBGColor || "#007bff",
                                      fontSize: "18px",
                                    }}
                                    onClick={() => fetchProductHistory(item.serial_number)}
                                  />
                                )}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item?.sac_code || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.service_category || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.service_method || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.service_type || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.execution_mode || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.product_quantity || 0}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {salesOrder?.currency_symbol || "₹"}{" "}
                              {formatIndianAmount(item.service_rate)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {salesOrder?.currency_symbol || "₹"}{" "}
                              {formatIndianAmount(item.gross_value || item.total_service_amount)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {(() => {
                                // discount_value is now the RAW input value (10 for 10% or 500 for ₹500)
                                const discountValueInput = parseFloat(item.discount_value) || 0;
                                const discountType = item.discount_type || "currency";

                                    if (
                                      discountType === "%" ||
                                      discountType === "percentage"
                                    ) {
                                      // Display raw percentage value
                                      return `${formatIndianAmount(discountValueInput)}%`;
                                    } else {
                                      // Display "Flat" for currency/flat discounts
                                      return "Flat";
                                    }
                              })()}
                            </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                              {(() => {
                                    // Calculate the actual discount amount from raw discount_value
                                    const discountValueInput =
                                      parseFloat(item.discount_value) || 0;
                                    const discountType =
                                      item.discount_type || "currency";
                                    const grossValue =
                                      parseFloat(
                                        item.gross_value ||
                                          item.total_service_amount ||
                                          0,
                                      ) || 0;

                                    let calculatedDiscountAmount = 0;
                                    if (
                                      discountType === "%" ||
                                      discountType === "percentage"
                                    ) {
                                      // Calculate: (gross * percentage) / 100
                                      calculatedDiscountAmount =
                                        (grossValue * discountValueInput) / 100;
                                } else {
                                      // Currency discount - use raw value directly (capped at gross)
                                      calculatedDiscountAmount = Math.min(
                                        discountValueInput,
                                        grossValue,
                                      );
                                    }

                                return `${salesOrder?.currency_symbol || "₹"}${formatIndianAmount(calculatedDiscountAmount)}`;
                              })()}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {salesOrder?.currency_symbol || "₹"}{" "}
                              {formatIndianAmount(item.total_service_net_amount)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.gst_percentage || "-"}
                              {item.gst_percentage ? "%" : ""}
                            </td>
                            {/* <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.composition_gst_percentage || "-"}
                              {item.composition_gst_percentage ? "%" : ""}
                            </td> */}
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.sgst_percentage || "-"}
                              {item.sgst_percentage ? "%" : ""}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.cgst_percentage || "-"}
                              {item.cgst_percentage ? "%" : ""}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.igst_percentage || "-"}
                              {item.igst_percentage ? "%" : ""}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              { "₹"}{" "}
                              {formatIndianAmount(item.gst_amount || item.gst_value)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {salesOrder?.currency_symbol || "₹"}{" "}
                              {formatIndianAmount(item.total_service_amount)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              {item.shipping_address ? (
                                <span style={{ fontSize: "0.7rem" }}>
                                  {(() => {
                                    // Handle both array and object cases
                                    const address = Array.isArray(item.shipping_address) 
                                      ? item.shipping_address[0] 
                                      : item.shipping_address;
                                    
                                    if (!address) return "-";
                                    
                                    return [
                                      address.address,
                                      address.city && `, ${address.city}`,
                                      address.state && `, ${address.state}`,
                                      address.pin_code || address.pincode ? ` - ${address.pin_code || address.pincode}` : ""
                                    ].filter(Boolean).join("");
                                  })()}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderBottom: "1px solid #eee",
                                maxWidth: "200px",
                              }}
                            >
                              <Tooltip
                                  title={item.reported_complaints || "-"}
                                arrow
                                placement="top"
                                disableHoverListener={!item.reported_complaints || item.reported_complaints === "-"}
                              >
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                  }}
                                >
                                  {item.reported_complaints || "-"}
                                </span>
                              </Tooltip>
                            </td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={23}
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee",
                            textAlign: "center",
                            color: "#666",
                            paddingTop: "20px",
                            paddingBottom: "20px",
                          }}
                        >
                          No items added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <hr />
              <>
                <h5 className="mb-3">Retention Details</h5>
                  <Row className="ms-3">
                  <Col md={6}>
                    <p>
                        <strong>Retention Type:</strong>{" "}
                      {salesOrder?.retention_type
                        ? salesOrder.retention_type.charAt(0).toUpperCase() +
                          salesOrder.retention_type.slice(1)
                        : "-"}
                    </p>
                  </Col>
                  <Col md={6}>
                      {salesOrder?.retention_type == "percentage" && (
                    <p>
                          <strong>Percentage:</strong>{" "}
                      {salesOrder?.retention_percentage
                        ? `${salesOrder.retention_percentage}%`
                        : "-"}
                    </p>
                      )}
                    </Col>
                  </Row>
                  <Row className="ms-3">
                    <Col>
                    <p>
                        <strong> Retention Amount:</strong>{" "}
                      {salesOrder?.retention_amount
                        ? `${salesOrder?.currency_symbol || "₹"}${formatIndianAmount(salesOrder.retention_amount)}`
                        : "-"}
                    </p>
                  </Col>

                    <Col>
                      <p>
                        <strong> Follow Up Date:</strong>{" "}
                        {(() => {
                          const retentionFollowUpDate =
                            getFollowUpDateByCategory("Retention", true);
                          if (retentionFollowUpDate) {
                            return formatDateDDMMYYYY(retentionFollowUpDate);
                          }
                          return "-";
                        })()}
                      </p>
                    </Col>
                  </Row>
                  <hr />
                </>
   
            {/* Terms & Conditions and Notes & Attachments */}
            <hr />
            <Row className="g-4">
              <Col md={6}>
                <h5 className="mb-3">Terms & Conditions</h5>
                <div
                  className="p-3"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    border: "1px solid #eee",
                  }}
                >
                  {(() => {
                    // Group terms by type to handle multiple options per type
                    const groupedTerms: Record<string, string[]> = {};
                    salesOrder?.terms_and_conditions?.forEach(
                      (item: any) => {
                        if (item.type) {
                          if (!groupedTerms[item.type]) {
                            groupedTerms[item.type] = [];
                          }
                          // Handle both string and array options
                          if (Array.isArray(item.options)) {
                            item.options.forEach((opt: string) => {
                              if (
                                opt &&
                                !groupedTerms[item.type].includes(opt)
                              ) {
                                groupedTerms[item.type].push(opt);
                              }
                            });
                          } else if (item.options) {
                            // If options is a string, check if it's newline-separated
                            const options = item.options
                              .split("\n")
                              .filter((opt: string) => opt.trim());
                            options.forEach((opt: string) => {
                              if (
                                opt &&
                                !groupedTerms[item.type].includes(opt)
                              ) {
                                groupedTerms[item.type].push(opt);
                              }
                            });
                          }
                        }
                          },
                    );

                    if (Object.keys(groupedTerms).length === 0) {
                      return (
                            <p
                              className="text-muted"
                              style={{ fontSize: "0.75rem" }}
                            >
                          No terms and conditions available
                        </p>
                      );
                    }

                    return Object.entries(groupedTerms).map(
                      ([type, options], idx) => (
                        <div key={idx} className="mb-3">
                          <strong
                            style={{
                              fontSize: "0.75rem",
                              display: "block",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {type}
                          </strong>
                          <ul
                            style={{
                              fontSize: "0.7rem",
                              color: "#666",
                              marginLeft: "20px",
                              paddingLeft: "0",
                              listStyle: "disc",
                            }}
                          >
                            {options.map(
                              (option: string, optIdx: number) => (
                                <li
                                  key={optIdx}
                                  style={{ marginBottom: "4px" }}
                                >
                                  {option}
                                </li>
                                  ),
                            )}
                          </ul>
                              {idx < Object.keys(groupedTerms).length - 1 && (
                            <hr
                              style={{
                                width: "100%",
                                marginTop: "12px",
                              }}
                            />
                          )}
                        </div>
                          ),
                    );
                  })()}
                </div>
                <div className="mt-3">
                <p>
                  <strong>Payment Terms Amount :</strong>{" "}
                  {salesOrder?.payment_terms_amount ? `${salesOrder?.currency_symbol || "₹"}${formatIndianAmount(salesOrder?.payment_terms_amount)}` : "-"}
                </p>
                </div>
              </Col>
          
              <Col md={6}>
                <div
                  className="p-3"
                  style={{
                    backgroundColor: MecordColors.ActiveBackground,
                    borderRadius: "10px",
                  }}
                >
                  <div className="mb-2 fw-semibold" style={{ fontSize: "13px" }}>Service</div>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Sub Total (Before Discount) :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(
                          salesOrder?.service_sub_total ?? salesOrder?.sub_total_before_discount ?? 0,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Total Discount Value :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(
                          salesOrder?.service_discount_value ?? salesOrder?.total_discount_value ?? 0,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Net Taxable Value :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(
                          salesOrder?.service_net_value ?? salesOrder?.total_net_value ?? 0,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Total GST Value :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(salesOrder?.service_gst_value ?? salesOrder?.total_gst_value ?? 0)}
                      </p>
                    </Col>
                  </Row>

                  <hr />
                  <div className="mb-2 fw-semibold" style={{ fontSize: "13px" }}>Accessories</div>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Sub Total (Before Discount) :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(
                          salesOrder?.accessory_sub_total ?? 0,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Total Discount Value :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(
                          salesOrder?.accessory_discount_value ?? 0,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Net Taxable Value :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(
                          salesOrder?.accessory_net_value ?? 0,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Total GST Value :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(salesOrder?.accessory_gst_value ?? 0)}
                      </p>
                    </Col>
                  </Row>

                  <hr />
                  <Row>
                    <Col md={6}>
                      <p>
                        <strong>Grand Total Amount :</strong>
                      </p>
                    </Col>
                    <Col md={6} className="text-end">
                      <p className="fw-bold" style={{ fontSize: "1.2rem" }}>
                        {salesOrder?.currency_symbol || "₹"}{" "}
                        {formatIndianAmount(salesOrder?.grand_total_amount || 0)}
                      </p>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            <hr />

            {/* Totals Section */}
            <Row className="mb-4">
              <Col md={6}>
                <h5 className="mb-3">Notes & Attachments</h5>
                <Table bordered className="bg-white">
                  <tbody>
                    <tr>
                      <td
                        style={{
                          minWidth: "70px",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Notes
                      </td>
                          <td style={{ whiteSpace: "pre-wrap" }}>{salesOrder?.notes || "-"}</td>
                    </tr>
                  </tbody>
                </Table>
                {salesOrder?.attachments &&
                salesOrder.attachments.length > 0 ? (
                  <div className="mt-3">
                        <strong className="d-block mb-2">Attachments:</strong>
                        <AttachmentList attachments={salesOrder.attachments} />
        
                  </div>
                ) : (
                  <p className="text-muted mt-2">No attachments</p>
                )}
                    <div className="mt-3"><strong>Attachment Description:</strong> <p className="text-muted ms-2 mt-1">{salesOrder?.attachment_description}</p></div>

              </Col>
            </Row>
         {/* Status Remarks & History */}
         <Row className="mt-3">
              <h5 className="mt-3 mb-3">Status Remarks & History</h5>
              <Col lg={6} sm={12} md={12} className="mb-3">
                <div className="followup-box">
                  <div className="followup-scroll">
                    {transformStatusRemarks(
                          salesOrder?.status_remarks_and_history,
                    ).length > 0 ? (
                      transformStatusRemarks(
                            salesOrder?.status_remarks_and_history,
                      ).map((item, index) => (
                        <div className="followup-card" key={index}>
                          <div className="d-flex align-items-start gap-3">
                            <div
                              className="avatar-circle"
                              style={{
                                backgroundColor: item.color || "#4F46E5",
                              }}
                            >
                              {item.initials}
                            </div>

                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2">
                                <strong>{item.employee_name}</strong>
                                <span
                                  className="status-badge"
                                  style={{
                                    backgroundColor: item.statusColor,
                                  }}
                                >
                                  {item.status}
                                </span>
                              </div>

                              <small className="text-muted">
                                {item.date} • {item.time}
                              </small>
                            </div>
                          </div>

                          <div className="mt-2 comment-text">
                            {item.comment}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted py-3">
                        No status remarks available
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <hr />
            {/* Back Button */}
            <Row className="mt-3">
              <Col className="d-flex justify-content-center gap-3">
                    {/* <CancelBackButton
                  to={getBackPath()}
                  label="Cancel"
                /> */}
                    <CancelButton
                      redirectTo={getBackPath()}
                      showConfirmation={false}
                />
              </Col>
            </Row>
            </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Product History Modal */}
      <ProductHistoryModal
        show={showHistoryModal}
        onHide={() => {
          setShowHistoryModal(false);
          setProductHistory(null);
          setSelectedSerialNo("");
        }}
        serialNo={selectedSerialNo}
        productHistory={productHistory}
        loading={loadingHistory}
      />

      {/* Service Description Modal (Info icon) */}
      <Modal
        show={showDescriptionModal}
        onHide={() => {
          setShowDescriptionModal(false);
          setDescriptionModalContent("");
          setDescriptionModalServiceName("");
        }}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: tableStyles.header.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title
            style={{
              fontFamily: tableStyles.header.fontFamily,
              fontSize: tableStyles.header.fontSize,
              fontWeight: tableStyles.header.fontWeight,
            }}
          >
            Service Description
            {descriptionModalServiceName ? ` - ${descriptionModalServiceName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            fontFamily: filterPopoverStyles.fontFamily,
            fontSize: filterPopoverStyles.fontSize,
            overflow: "hidden",
            maxWidth: "100%",
          }}
        >
          {descriptionModalContent ? (
            <div
              className={`${DETAILS_BG} description-modal-content`}
              style={{
                minHeight: "80px",
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
                overflow: "auto",
                maxWidth: "100%",
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(descriptionModalContent),
              }}
            />
          ) : (
            <div
              className="text-muted"
              style={{
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
              }}
            >
              No description available.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              borderColor: filterPopoverStyles.cancelButton.borderColor,
              color: filterPopoverStyles.cancelButton.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowDescriptionModal(false);
              setDescriptionModalContent("");
              setDescriptionModalServiceName("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Additional Details Modal (Info icon) */}
      <Modal
        show={showAdditionalDetailsModal}
        onHide={() => {
          setShowAdditionalDetailsModal(false);
          setAdditionalDetailsContent("");
          setAdditionalDetailsServiceName("");
        }}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: tableStyles.header.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title
            style={{
              fontFamily: tableStyles.header.fontFamily,
              fontSize: tableStyles.header.fontSize,
              fontWeight: tableStyles.header.fontWeight,
            }}
          >
            Additional Details
            {additionalDetailsServiceName ? ` - ${additionalDetailsServiceName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            fontFamily: filterPopoverStyles.fontFamily,
            fontSize: filterPopoverStyles.fontSize,
            overflow: "hidden",
            maxWidth: "100%",
          }}
        >
          {additionalDetailsContent ? (
            <div
              className={`${DETAILS_BG} description-modal-content`}
              style={{
                minHeight: "80px",
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
                overflow: "auto",
                maxWidth: "100%",
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(additionalDetailsContent),
              }}
            />
          ) : (
            <div
              className="text-muted"
              style={{
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
              }}
            >
              No additional details available.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              borderColor: filterPopoverStyles.cancelButton.borderColor,
              color: filterPopoverStyles.cancelButton.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsContent("");
              setAdditionalDetailsServiceName("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ViewServiceAccessoriesModal
        show={showAccessoriesModal}
        onHide={() => {
          setShowAccessoriesModal(false);
          setViewAccessories([]);
          setAccessoriesServiceName("");
          setAccessoriesServiceCategory("");
        }}
        serviceName={accessoriesServiceName}
        serviceCategory={accessoriesServiceCategory}
        accessories={viewAccessories}
        currencySymbol="₹"
      />
    </>
  );
};

export default ViewSalesOrderPage;
